/**
 * styles.js — CSS for the widget, scoped to its shadow root.
 *
 * Per-event look is driven entirely by custom properties set on the host
 * element, so this sheet is a constant and can be parsed once.
 */

export const CSS = `
/*
 * contain:layout would make the host a containing block for fixed-position
 * descendants, which would trap the full-page petal field inside the small
 * banner box. Style containment is kept; layout containment is not needed
 * because the host is position:fixed and already out of the page's flow.
 */
:host {
  display: block;
  contain: style;
}

.card {
  position: relative;
  pointer-events: auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: var(--tc-width, 180px);
  max-width: 40vw;
  padding: 14px 14px 10px;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
  animation: tc-in 520ms cubic-bezier(.2, .8, .3, 1) both;
}

/*
 * Petal field — the full viewport, drawn beneath the banner card.
 *
 * pointer-events:none throughout, so it never intercepts a click meant for the
 * page: it is decoration laid over the portal, not a layer the user can hit.
 * The whole container is removed from the DOM once the last petal lands.
 */
.petals {
  position: fixed;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.petal {
  position: absolute;
  top: -14px;
  width: var(--s, 9px);
  height: var(--s, 9px);
  background: var(--c, #ff9933);
  border-radius: 50% 0 50% 0;
  opacity: 0;
  /* Keeps a petal readable when it drifts over a band of its own colour. */
  box-shadow: 0 1px 2px rgba(11, 16, 32, .30);
  will-change: transform, opacity;
  animation: tc-fall var(--d, 3.4s) cubic-bezier(.35, .05, .5, 1) var(--dl, 0s) 1 both;
}

@keyframes tc-fall {
  0%   { opacity: 0;   transform: translate3d(0, -14px, 0) rotate(0deg) scale(.85); }
  12%  { opacity: .95; }
  75%  { opacity: .9; }
  100% { opacity: 0;   transform: translate3d(var(--x, 12px), var(--fall, 150px), 0) rotate(var(--r, 320deg)) scale(1); }
}

.media {
  display: block;
  width: 100%;
  text-decoration: none;
  border: 0;
}

.img {
  display: block;
  width: 100%;
  height: auto;
  border: 0;
  border-radius: 8px;
  filter: drop-shadow(0 6px 14px rgba(11, 16, 32, .22));
}

.title {
  margin: 8px 0 0;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.25;
  text-align: center;
  letter-spacing: .2px;
  color: var(--tc-title, #ff9800);
}

/*
 * Gradient text. The shadow is drawn behind the glyphs and escapes as a thin
 * halo, which is what keeps a white gradient stop readable on a white portal
 * background — the failure mode of the original tricolour heading.
 */
.title.grad {
  background-image: var(--tc-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  -webkit-text-stroke: .6px rgba(0, 0, 0, .55);
  text-shadow: 0 1px 3px rgba(0, 0, 0, .4);
  paint-order: stroke fill;
}

.close {
  position: absolute;
  top: 0;
  right: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  margin: 0;
  border: 0;
  border-radius: 6px;
  background: #dc3545;
  color: #fff;
  font: 700 15px/1 system-ui, -apple-system, sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: .85;
  transition: opacity 150ms ease, transform 150ms ease;
  pointer-events: auto;
}

.close:hover { opacity: 1; transform: scale(1.06); }
.close:focus-visible { outline: 2px solid #fff; outline-offset: 2px; opacity: 1; }

@keyframes tc-in {
  from { opacity: 0; transform: translateY(16px) scale(.965); }
  to   { opacity: 1; transform: none; }
}

@media (max-width: 480px) {
  .card { width: var(--tc-width-sm, 120px); padding: 12px 12px 8px; }
  .title { font-size: 12px; }
}

@media (prefers-reduced-motion: reduce) {
  .card { animation: none; }
  .close { transition: none; }
  .petals { display: none; }
}
`;
